// Libs
import { Img, Flex, Button, IconToggleStartLine, IconInfoLine } from '@instructure/ui';
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import i18n from '~/thirdParties/i18n';
import { useLocation } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseSelect, MobileMenuDialog } from '~/components/common';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { LanguageEnum, StorageEnum } from '~/utils/enum/base';
import { studentRoute } from '~/utils/routes/student';
import { adminRoute } from '~/utils/routes/admin';
import { IBaseSelectOption } from '~/utils/interface/common';
import { mockHelpUrl } from '~/utils/mockData/common';
import { authRoute } from '~/utils/routes/auth';
import { RoleEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './Header.module.scss';
import './Header.scss';
import { images, icons } from '~/assets';
import { teacherRoute } from '~/utils/routes/teacher';

const cx = classNames.bind(styles);

const Header = () => {
  //#region Destructuring Props
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/navbar', 'common/base']);
  const navigate = useNavigate();
  const location = useLocation();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const isAuthPage = location.pathname === authRoute.base;
  const languageOptions: IBaseSelectOption[] = [
    {
      value: LanguageEnum.EN,
      label: 'English',
      renderBeforeLabel: <img src={icons.commonIconEnglishFlag} alt={t('common_alt_img')} className={cx('iconFlag')} />,
    },
    {
      value: LanguageEnum.IT,
      label: 'Italian',
      renderBeforeLabel: <img src={icons.commonIconItalianFlag} alt={t('common_alt_img')} className={cx('iconFlag')} />,
    },
    {
      value: LanguageEnum.ES,
      label: 'Spanish',
      renderBeforeLabel: <img src={icons.commonIconSpainFlag} alt={t('common_alt_img')} className={cx('iconFlag')} />,
    },
  ];
  const [languageSelected, setLanguageSelected] = useState<string>(() => {
    return localStorage.getItem(StorageEnum.LANGUAGE) || languageOptions[0].value;
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClose, setIsMenuClose] = useState(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const language = localStorage.getItem(StorageEnum.LANGUAGE);
    if (language) {
      i18n.changeLanguage(language);
      setLanguageSelected(language);
    }
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/auth';
  };

  const handleChangeLanguage = (option?: IBaseSelectOption) => {
    if (!option) return;
    setLanguageSelected(option.value);
    localStorage.setItem(StorageEnum.LANGUAGE, option.value);
    i18n.changeLanguage(option.value);
  };

  const handleClickLogo = () => {
    switch (currentRole) {
      case RoleEnum.STUDENT:
        navigate(`${studentRoute.base}`);
        break;
      case RoleEnum.TEACHER:
        navigate(`${teacherRoute.base}`);
        break;
      default:
        navigate(`${studentRoute.base}`);
    }
  };

  const handleOpenMenu = () => {
    setIsMenuOpen(true);
    document.body.classList.add(cx('overflow-hidden'));
  };

  const handleCloseMenu = () => {
    setIsMenuClose(true);

    setTimeout(() => {
      setIsMenuOpen(false);
      setIsMenuClose(false);
      document.body.classList.remove(cx('overflow-hidden'));
    }, 400);
  };

  const handleClickHelp = () => {
    window.open(mockHelpUrl);
  };

  const handleNavigateTuitionArea = () => {
    navigate(`${adminRoute.base}${adminRoute.tuitionArea}`);
  };

  const handleNavigateAdministrativeArea = () => {
    navigate(`${adminRoute.base}${adminRoute.administrativeArea}`);
  };
  //#endregion Handle Function

  return (
    <>
      <div className={cx('topNavbar')}>
        <div className={cx('navbarContainer')}>
          <Flex justifyItems='space-between' alignItems='center' height={'100%'} gap='large'>
            {!isAuthPage ? (
              <div className={cx('logo')} onClick={handleClickLogo}>
                <Img src={images.commonLogoFull} alt={t('common_alt_img')} constrain='contain' />
              </div>
            ) : (
              <div></div>
            )}

            <Flex justifyItems='space-between' alignItems='center' height='100%'>
              <Flex.Item shouldShrink={false}>
                <Flex gap='small' alignItems='center'>
                  {!isAuthPage && currentRole === RoleEnum.ADMIN && (
                    <>
                      <Button
                        onClick={handleNavigateTuitionArea}
                        renderIcon={
                          <img
                            className={cx('iconInput')}
                            src={icons.commonIconTuitionArea}
                            alt={t('common_alt_img')}
                          />
                        }
                      >
                        <div className={cx('navButtonText')}>{t('tuition_area', { ns: 'common/navbar' })}</div>
                      </Button>

                      <Button
                        onClick={handleNavigateAdministrativeArea}
                        renderIcon={
                          <img className={cx('iconInput')} src={icons.commonIconExamArea} alt={t('common_alt_img')} />
                        }
                      >
                        <div className={cx('navButtonText')}>{t('administrative_area', { ns: 'common/navbar' })}</div>
                      </Button>
                    </>
                  )}

                  <div className={cx('headerSelect')}>
                    <BaseSelect value={languageSelected} options={languageOptions} onChange={handleChangeLanguage} />
                  </div>

                  <Button renderIcon={<IconInfoLine />} onClick={handleClickHelp}>
                    {t('tooltip_action_help', { ns: 'common/navbar' })}
                  </Button>

                  {currentRole && !isAuthPage && (
                    <Button renderIcon={<IconToggleStartLine />} onClick={handleLogout}>
                      {t('action_logout', { ns: 'common/navbar' })}
                    </Button>
                  )}
                </Flex>
              </Flex.Item>
            </Flex>
          </Flex>
        </div>
      </div>

      <div className={cx('mobileNav')}>
        {!isAuthPage ? (
          <div className={cx('logo')} onClick={handleClickLogo}>
            <Img src={images.commonLogoFull} constrain='contain' alt={t('common_alt_img')} />
          </div>
        ) : (
          <div></div>
        )}

        <div className={cx('menuIcon')} onClick={handleOpenMenu}>
          <img
            src={icons.commonIconMenuMobile}
            alt={t('common_alt_img', { ns: 'common/base' })}
            className={cx('menuMobileIcon')}
          />
        </div>

        <MobileMenuDialog
          isMenuOpen={isMenuOpen}
          isMenuClose={isMenuClose}
          onClose={handleCloseMenu}
          onHelpClick={handleClickHelp}
          onChangeLanguage={handleChangeLanguage}
          languageOptions={languageOptions}
          languageSelected={languageSelected}
          onLogout={handleLogout}
          showLogout={currentRole && !isAuthPage ? true : false}
          showAdminMenu={!isAuthPage && currentRole === RoleEnum.ADMIN}
        />
      </div>
    </>
  );
};

export default Header;
